/* eslint-disable @typescript-eslint/require-await */
import { action, set } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { next } from '@ember/runloop';
import { inject as service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type AbilitiesService from 'ember-can/services/abilities';
import { task } from 'ember-concurrency';
import type EmployeeModel from 'garaje/models/employee';
import type GenericResourceModel from 'garaje/models/generic-resource';
import type MapFeatureModel from 'garaje/models/map-feature';
import type NeighborhoodModel from 'garaje/models/neighborhood';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type MetricsService from 'garaje/services/metrics';
import type StateService from 'garaje/services/state';
import type { ResourceAmenityType } from 'garaje/types/generic-resources/ResourceTypeConfig';
import employeesSearcherTask, { type EmployeeSearcherTask } from 'garaje/utils/employees-searcher';
import urlBuilder from 'garaje/utils/url-builder';

interface GenericResourceFormArgs {
  feature: MapFeatureModel;
  mapFeatures: MapFeatureModel[];
  floorMapFeatures: MapFeatureModel[];
  additionalDetails: {
    employees: EmployeeModel[];
    neighborhoods: NeighborhoodModel[];
  };
  isDraftMap: boolean;
  removeFeature: (feature: MapFeatureModel) => void;
  back: () => void;
  updateResourceOverview: (action: string, feature: MapFeatureModel) => void;
  setMapFeatures: (features: MapFeatureModel[]) => void;
  onSelectedFeatureChange: (feature: MapFeatureModel | null) => void;
}

export default class GenericResourceForm extends Component<GenericResourceFormArgs> {
  @service declare state: StateService;
  @service declare store: StoreService;
  @service declare metrics: MetricsService;
  @service declare router: RouterService;
  @service declare abilities: AbilitiesService;
  @service declare ajax: AjaxFetchService;

  @tracked availableAmenities: ResourceAmenityType[] = [];
  @tracked searchedEmployees: EmployeeModel[] = [];
  @tracked employeeError: string[] = [];
  @tracked nameError: string[] = [];
  @tracked inputElement?: HTMLInputElement;
  @tracked showActionMenu = false;
  @tracked searchedNeighborhoods: NeighborhoodModel[] = this.neighborhoods;

  get selectedEmployee(): undefined | { id: string; name: string } {
    const employeeId = this.resource?.assignments?.[0]?.employeeId;

    if (!employeeId) {
      return;
    }

    const employee = this.store.peekRecord('employee', employeeId);

    if (!employee) {
      return { id: String(employeeId), name: String(employeeId) };
    }

    return { id: String(employeeId), name: employee.name };
  }

  get neighborhoods(): NeighborhoodModel[] {
    return this.args.additionalDetails.neighborhoods;
  }

  get hasResourceError(): boolean {
    return !!(this.nameError.length || this.employeeError.length);
  }

  get cannotManageResource(): boolean {
    return false;
  }

  get resource(): GenericResourceModel | null {
    if (!this.args.feature.externalId) return null;
    return this.store.peekRecord('generic-resource', this.args.feature.externalId);
  }

  get selectedAmenities(): ResourceAmenityType[] {
    const amenityIds = (this.resource?.amenities || []).map((a) => a.id);
    return this.availableAmenities.filter((a) => amenityIds.includes(a.resourceTypeAmenityId));
  }

  get employeesToShow(): EmployeeModel[] {
    if (this.searchedEmployees.length) {
      return this.searchedEmployees;
    }
    return this.args.additionalDetails.employees;
  }

  get cannotEditMap(): boolean {
    return this.abilities.cannot('edit maps');
  }

  get isActionMenuEnabled(): boolean {
    const { feature } = this.args;

    // Only show the action menu if the parking spot was previously unplaced.
    // A resource with an ID means it already exists on the backend.
    // A map feature with an external id that begins with 'temp' means it has only been created client side
    // and the underlying resource hasn't been created yet.

    // @ts-ignore - isNew is a computed property on MapFeatureModel
    return !!this.resource?.id && feature.isNew && !!feature.externalId && !feature.externalId.startsWith('temp-');
  }

  @action
  toggleActionMenu(): void {
    this.showActionMenu = !this.showActionMenu;
  }

  @action
  closeActionMenu(): void {
    this.showActionMenu = false;
  }

  @action
  inserted(): void {
    void this.getAvailableAmenities.perform();
  }

  @action
  inputInserted(element: HTMLInputElement): void {
    this.inputElement = element;
    this.focusInput();
  }

  @action
  focusInput(): void {
    next(() => {
      this.inputElement?.focus();
    });
  }

  setResourceHasError(): void {
    const { feature } = this.args;

    if (feature.isDestroyed) {
      return;
    }

    if (this.hasResourceError) {
      set(feature, 'hasError', true);
      return;
    }

    set(feature, 'hasError', false);
  }

  @action
  validateNameField(): void {
    const { feature } = this.args;
    const changedAttrs = feature.changedAttributes();

    if (isEmpty(feature.name) && (Object.keys(changedAttrs).includes('name') || feature.hasError || !feature.isValid)) {
      this.nameError = ['Please enter a name'];
    } else if (feature.name?.length > 40) {
      this.nameError = [''];
    } else {
      this.nameError = [];
    }
    this.setResourceHasError();
  }

  clearEmployeeErrors(): void {
    this.employeeError = [];
  }

  // This action doesn't actually delete a resource - it just removes it from the map client-side.
  // This is used primarily to reset an originally unplaced resource to its original state after being placed on the map.
  @action
  handleRemoveResourceFromMap(): void {
    const { feature, mapFeatures, setMapFeatures, onSelectedFeatureChange } = this.args;

    this.store.unloadRecord(feature);
    onSelectedFeatureChange(null);
    // Fix: Filter by externalId instead of id since newly placed features don't have an id yet
    setMapFeatures(mapFeatures.filter((mapFeature: MapFeatureModel) => mapFeature.externalId !== feature.externalId));
  }

  @action
  onResourceDelete(): void {
    const { feature, removeFeature } = this.args;

    this.resource?.deleteRecord();
    removeFeature(feature);
  }

  @action
  onResourceNameChange(name: string): void {
    const { feature, updateResourceOverview } = this.args;

    feature.name = name;

    if (this.resource) {
      this.resource.name = name;
    }

    if (updateResourceOverview) {
      updateResourceOverview('update', feature);
    }

    this.validateNameField();
  }

  @action
  onNeighborhoodSelect(option: NeighborhoodModel): void {
    this.resource!.neighborhoodIds = [{ id: Number(option.id) }];
  }

  get selectedNeighborhood(): NeighborhoodModel | null {
    const neighborhoodId = this.resource?.neighborhoodIds?.[0]?.id;
    if (!neighborhoodId) {
      return null;
    }
    return this.store.peekRecord('neighborhood', neighborhoodId);
  }

  @action
  clearNeighborhoodSelection(): void {
    this.resource!.neighborhoodIds = [];
  }

  @action
  onNeighborhoodSearch(value: string): void {
    this.searchedNeighborhoods = this.neighborhoods.filter((neighborhood) => {
      return neighborhood.name.toLowerCase().includes(value.toLowerCase());
    });
  }

  @action
  setSelectedAmenities(selectedAmenities: ResourceAmenityType[]): void {
    if (!this.resource) {
      return;
    }

    this.resource.amenities = selectedAmenities.map((a) => ({ id: a.resourceTypeAmenityId }));
  }

  @action
  returnFalseValid(): boolean {
    return false;
  }

  @action
  onEmployeeSelect(option: string | EmployeeModel): void {
    if (option === 'loadMore' || typeof option === 'string') {
      return;
    }

    this.clearEmployeeErrors();

    if (this.resource) {
      this.resource.assignments = [{ employeeId: Number(option.id) }];
    }
  }

  @(employeesSearcherTask({
    filter: {
      deleted: false,
    },
    prefix: true,
  }).restartable())
  _searchEmployeesTask!: EmployeeSearcherTask;

  employeesSearcherTask = task({ restartable: true }, async (term: string) => {
    const extraFilters = {
      locations: this.state.currentLocation.id,
      deleted: false,
    };
    this.searchedEmployees = await this._searchEmployeesTask.perform(term, extraFilters);
  });

  onEmployeeSearchTask = task({ restartable: true }, async (searchTerm: string) => {
    if (searchTerm) {
      await this.employeesSearcherTask.perform(searchTerm);
    }
    const validEmployee = this.searchedEmployees.find(
      (employee) => searchTerm.toLowerCase().trim() === employee.name?.toLowerCase(),
    );

    if (validEmployee) {
      return this.clearEmployeeErrors();
    }

    if (searchTerm === '' && this.resource) {
      this.resource.assignments = [];
    }

    if (searchTerm === '') {
      this.searchedEmployees = [];
      return;
    }

    this.employeeError = ["Employee doesn't exist"];
  });

  getAvailableAmenities = task({ restartable: true }, async () => {
    const resource = this.resource;

    if (!resource) {
      return;
    }

    const url = urlBuilder.rms.amenities(resource.resourceTypeId, this.state.currentLocation.id);

    const response: { data: ResourceAmenityType[] } = await this.ajax.request(url, {
      type: 'GET',
      contentType: 'application/json',
    });

    this.availableAmenities = response.data;
  });

  validateAssignedEmployeeTask = task({ restartable: true }, async () => {
    this.clearEmployeeErrors();

    if (!this?.resource?.assignments?.[0]) {
      return;
    }

    const employeeId = String(this.resource.assignments[0].employeeId);
    const employees = await this.store.query('employee', {
      filter: { deleted: false, id: employeeId, locations: this.state.currentLocation.id },
    });
    const employee = employees.find((employee) => employee.id === employeeId);

    if (!employee) {
      this.resource.assignments = [];
      this.employeeError = ["Employee doesn't exist"];
      this.setResourceHasError();
    }
  });

  onEmployeeFocusOutTask = task({ drop: true }, async () => {
    void this.validateAssignedEmployeeTask.perform();
  });
}
